import { createEvent, createStore, sample } from 'effector';
import { setSceneId, stopVirtualTour } from './appState';
import { assistantChatReset } from './assistantEvents';
import { sceneLoadingReset } from './sceneLoading';
import { logEvent as logMemoryEvent } from '../debug/memoryTracker';

export type SceneStackEntry = {
  sceneId: string;
  poiFilterId: string;
};

export const enterScene = createEvent<SceneStackEntry>();
export const goBack = createEvent();
export const resetNavigation = createEvent();
export const rebuildStack = createEvent<SceneStackEntry[]>();
/**
 * Replace the TOP entry of the stack (keep the parent). Used to switch between
 * sibling era scenes (`[kaaba, eraF]` → `[kaaba, eraE]`) in one remount,
 * preserving the parent for `goBack`. Unlike `rebuildStack`, it fires the full
 * navigation side-effects (`setSceneId` + `sceneLoadingReset` + `stopVirtualTour`),
 * mirroring `enterScene`. See `src/state/historicalScenes.ts`.
 */
export const replaceScene = createEvent<SceneStackEntry>();

// --- PoiArtifact overlay (3D-артефакт открывается поверх основной сцены по POI-клику) ---
export const openPoiArtifact = createEvent<{ sceneId: string }>();
export const closePoiArtifact = createEvent();

export const $poiArtifactId = createStore<string | null>(null)
  .on(openPoiArtifact, (_, { sceneId }) => sceneId)
  .on(closePoiArtifact, () => null)
  .on(resetNavigation, () => null);

export const $isPoiArtifactOpen = $poiArtifactId.map((id) => id !== null);

// Stop virtual tour when opening PoiArtifact
sample({
  clock: openPoiArtifact,
  target: stopVirtualTour,
});

// Close mobile keyboard on PoiArtifact open/close
openPoiArtifact.watch(() => {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
});

closePoiArtifact.watch(() => {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
});

// --- Main scene stack (not affected by PoiArtifact overlay) ---
export const $sceneStack = createStore<SceneStackEntry[]>([])
  .on(enterScene, (stack, entry) => [...stack, entry])
  .on(goBack, (stack) => (stack.length > 1 ? stack.slice(0, -1) : []))
  .on(resetNavigation, () => [])
  .on(rebuildStack, (_, stack) => stack)
  .on(replaceScene, (stack, entry) => [...stack.slice(0, -1), entry]);

export const $activeScene = $sceneStack.map(
  (stack) => stack[stack.length - 1] ?? null,
);

export const $activeSceneId = $activeScene.map(
  (entry) => entry?.sceneId ?? null,
);

export const $parentScene = $sceneStack.map(
  (stack) => (stack.length > 1 ? stack[stack.length - 2] : null),
);

// Sync poiFilterId → appState.sceneId on enter
sample({
  clock: enterScene,
  fn: (entry) => entry.poiFilterId,
  target: setSceneId,
});

// Reset loading state when entering a new scene
sample({
  clock: enterScene,
  fn: () => undefined,
  target: sceneLoadingReset,
});

// On goBack — sync sceneId to the new top of stack
sample({
  clock: goBack,
  source: $activeScene,
  filter: (scene): scene is SceneStackEntry => scene != null,
  fn: (scene) => scene.poiFilterId,
  target: setSceneId,
});

// Reset loading state on goBack
sample({
  clock: goBack,
  fn: () => undefined,
  target: sceneLoadingReset,
});

// On rebuildStack — sync sceneId to new top
sample({
  clock: rebuildStack,
  filter: (stack) => stack.length > 0,
  fn: (stack) => stack[stack.length - 1].poiFilterId,
  target: setSceneId,
});

// On replaceScene — replace stack top: full navigation side-effects (mirror
// enterScene) so the swapped-in era scene gets a clean loading state + remount.
sample({
  clock: replaceScene,
  fn: (entry) => entry.poiFilterId,
  target: setSceneId,
});
sample({
  clock: replaceScene,
  fn: () => undefined,
  target: sceneLoadingReset,
});
sample({
  clock: replaceScene,
  target: stopVirtualTour,
});

// Stop virtual tour when navigating to a different scene
sample({
  clock: enterScene,
  target: stopVirtualTour,
});

// Close mobile keyboard on any scene navigation
enterScene.watch(() => {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
});

goBack.watch(() => {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
});

replaceScene.watch(() => {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
});

// Reset chat on full navigation reset (return to map)
sample({
  clock: resetNavigation,
  target: assistantChatReset,
});

// CRITICAL (iOS OOM fix): reset scene loading state on return to map.
//
// Without this, `$sceneBindings` in appState.ts retains a strong reference to
// the entire SceneBindings object (scene, renderer, camera, controls,
// sparkRenderer, sightManager, tellerAssistant) even after React unmounts the
// canvas and createScene.dispose() runs. The Three.js internals are disposed
// at GPU level via forceContextLoss, but the JS-side objects stay alive
// because the Effector store holds them — preventing JSC from garbage
// collecting ~100+ MB of splat infrastructure. On iOS this accumulation
// across a tab's lifetime triggers OOM-kill during the next scene mount.
//
// `sceneLoadingReset` fires on `enterScene` and `goBack` (lines 76, 92) but
// was NOT wired to `resetNavigation` — so the leak only manifested on the
// map-return path. Adding it here closes the gap.
sample({
  clock: resetNavigation,
  fn: () => undefined,
  target: sceneLoadingReset,
});

// Diagnostic: confirm the reset fires on return-to-map (verifiable via
// `?mem-debug=1` logs). If this line DOES appear in logs, the fix is wired
// correctly — if not, Vite HMR may be serving stale code.
resetNavigation.watch(() => {
  logMemoryEvent('resetNavigation → sceneLoadingReset (bindings cleared)');
});
